"use client";

import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { ArrowLeft, ClockFading } from "lucide-react";
import Link from "next/link";
import { BlogPost } from "@/actions/directus";
import ReactMarkdown from 'react-markdown';
import gfm from 'remark-gfm';
import { notFound } from "next/navigation";
import Image from "next/image";
import "./style.css";
import { Button } from "../ui/button";

export interface BlogPostData {
    id: number;
    posts_slug: string;
    languages_code: string;
    content: string;
    title: string;
    caption: string;
}

export const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 },
};

export function CustomButton({
    href = "/blog",
    goBackIcon = true,
}: {
    href?: string;
    goBackIcon?: boolean;
}) {
    return (
        <motion.div variants={itemVariants} className="pt-4 w-full text-left">
            <Link href={href} className="cursor-pointer">
                <Button variant="ghost" size="sm" className="group mb-4 pl-0 bg-[#F2F2F2] hover:bg-[#EBEBEB] border border-[#EBEBEB] hover:border-[#C9C9C9] text-[#171717] cursor-pointer">
                    {goBackIcon && <ArrowLeft className="mr-2 h-4 w-4 transition-transform group-hover:-translate-x-1" />}
                    Voltar
                </Button>
            </Link>
        </motion.div>
    );
}


export function BlogPostView({ post }: { post: BlogPost; }) {
    const [readingTime, setReadingTime] = useState("5 min de leitura");

    const postContent = post.translations[0];

    if (!postContent) {
        notFound();
    }

    useEffect(() => {
        const wordsPerMinute = 200;
        const wordCount = postContent.content.trim().split(/\s+/).length;
        const time = Math.ceil(wordCount / wordsPerMinute);
        setReadingTime(`${time} min de leitura`);
    }, [postContent.content]);

    const containerVariants = {
        hidden: { opacity: 0 },
        visible: {
            opacity: 1,
            transition: {
                staggerChildren: 0.1,
            },
        },
    };



    return (
        <motion.div className="max-w-3xl w-full py-8 px-4 lg:px-0" variants={containerVariants} initial="hidden" animate="visible">
            <CustomButton />
            <motion.div variants={itemVariants} className="space-y-2 mb-8">
                <h1 className="text-4xl font-bold tracking-tight ">{postContent.title}</h1>
                <p className="text-xl text-muted-foreground">{postContent.caption}</p>
            </motion.div>

            <motion.div
                variants={itemVariants}
                className="flex items-center gap-4 text-sm text-muted-foreground mb-4 border-b pb-6"
            >
                <div className="flex items-center">
                    <ClockFading className="mr-2 h-4 w-4" />
                    <p>{readingTime}</p>
                </div>
            </motion.div>
            <motion.div className="md:py-4">
                <img src={`https://cms.costao.com.br/assets/${post.image.id}`} alt={postContent.title} height={1080} width={1920} />
            </motion.div>
            <motion.div
                variants={itemVariants}
                className="prose prose-lg dark:prose-invert max-w-none"
            >
                <div className="my-4 text-primary blog-rmkd text-left ">
                    <ReactMarkdown remarkPlugins={[gfm]}>
                        {postContent.content}
                    </ReactMarkdown>
                </div>
            </motion.div>
        </motion.div>
    );
}

// Helper function to format content with Notion-like styling
function formatContent(content: string): string {
    // This is a simple implementation - for a real app, you might want to use a markdown parser
    // or a more sophisticated HTML processor

    let formatted = content.replace(/\n\n/g, "</p><p>");

    if (!formatted.startsWith("<p>")) {
        formatted = `<p>${formatted}</p>`;
    }

    return formatted;
}
export function BlogPostSkeleton() {
    return (
        <>
            <div className="max-w-3xl w-full py-8 animate-pulse">
                <div className="mb-8"></div>
                <div className="h-8 w-64 bg-neutral-200 rounded-md" />
            </div>

            <div className="space-y-4 mb-8">
                <div className="h-12 w-4xl max-w-[32rem] bg-neutral-200 rounded-md" />
                <div className="h-8 w-3/4 bg-neutral-200 rounded-md" />
            </div>

            <div className="flex items-center gap-4 mb-12 pb-6 border-b">
                <div className="h-5 w-40 bg-neutral-200 rounded-md" />
                <div className="h-5 w-40 bg-neutral-200 rounded-md" />
            </div>

            <div className="space-y-6">
                <div className="h-6 w-4xl max-w-[32rem] bg-neutral-200 rounded-md" />
                <div className="h-6 w-4xl max-w-[32rem] bg-neutral-200 rounded-md" />
                <div className="h-6 w-5/6 bg-neutral-200 rounded-md" />
                <div className="h-6 w-4xl max-w-[32rem] bg-neutral-200 rounded-md" />
                <div className="h-6 w-4/5 bg-neutral-200 rounded-md" />

                <div className="py-4"></div>

                <div className="h-6 w-4xl max-w-[32rem] bg-neutral-200 rounded-md" />
                <div className="h-6 w-4xl max-w-[32rem] bg-neutral-200 rounded-md" />
                <div className="h-6 w-3/4 bg-neutral-200 rounded-md" />
            </div>
        </>
    );
}
